import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, ApiError, call, type Schemas } from '../api/client';
import { useApi } from '../api/query';
import { SettingsNav } from '../components';
import { currentLanguage, t } from '../i18n';
import { clock, day } from '../lib/format';
import { can, useSession } from './session';
import { useLicence } from './shell/data';

/**
 * Helpers shared by every page: the shell context (server clock, counts), page exports,
 * route parameters, permission and licence checks, and the live SettingsNav.
 */

export type ShellStatus = Schemas['ShellStatus'];
export const ShellCtx = createContext<ShellStatus | undefined>(undefined);
/** Shell status (server, counts, licence banner, server time) loaded once by the app shell. */
export const useShell = () => useContext(ShellCtx);
/** Live figures are frozen: the connection is down, or it is up with no recent tick. */
export const liveFrozen = (shell: ShellStatus | undefined) => shell?.server.status === 'crit' || shell?.server.status === 'idle';

/**
 * Trading-server clock, ticking every second: `{ time: '14:32:05', date: 'Fri 23 Oct 2026' }`,
 * from the shell's server time and the server's time-zone offset.
 */
export function useServerClock() {
  const sh = useShell();
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const iv = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(iv);
  }, []);
  const base = sh?.server_time ? Date.parse(sh.server_time) : null;
  // `server_time` moves with the live `shell` topic: time passed counts from the latest value only
  const anchor = useRef({ base, at: Date.now() });
  if (anchor.current.base !== base) anchor.current = { base, at: Date.now() };
  const serverNow = base != null ? base + (Date.now() - anchor.current.at) : now;
  const offset = sh?.server_tz_offset_minutes ?? 0;
  // render the server wall clock: shift by the offset and format as UTC
  const shifted = serverNow + offset * 60_000;
  return { ms: serverNow, time: clock(shifted, 'UTC'), date: day(shifted, 'UTC'), offsetMinutes: offset };
}

/** Route query parameters with typed helpers (`login`, `id`, `symbol`, `server_id`, `tab`…). */
export function usePageParams() {
  const [q, setQ] = useSearchParams();
  return {
    get: (k: string) => q.get(k) ?? undefined,
    num: (k: string) => {
      const v = q.get(k);
      return v != null && v !== '' && !Number.isNaN(Number(v)) ? Number(v) : undefined;
    },
    set: (patch: Record<string, string | number | null | undefined>) => {
      const n = new URLSearchParams(q);
      for (const [k, v] of Object.entries(patch)) {
        if (v == null || v === '') n.delete(k);
        else n.set(k, String(v));
      }
      setQ(n, { replace: true });
    },
    all: Object.fromEntries(q.entries()),
  };
}

/** Permission check against the signed-in session. */
export function useCan() {
  const { session } = useSession();
  const lic = useLicence();
  return {
    can: (perm: string) => can(session, perm),
    /** trading-server actions, alerts and scheduled reports are paused in read-only mode */
    readOnly: lic.readOnly,
    session,
  };
}

export type ExportFormat = 'pdf' | 'xlsx' | 'csv';

/**
 * Page export (every page's Export menu): `POST /exports {page_id, format, language, params}`,
 * poll `GET /exports/{id}` until done, then download. `state` drives the menu's busy and error text.
 */
export function useExport(pageId: string) {
  const [state, setState] = useState<{ busy: ExportFormat | null; error: string | null; last: Schemas['Export'] | null }>({
    busy: null,
    error: null,
    last: null,
  });
  const run = useCallback(
    async (format: ExportFormat, params: Record<string, unknown> = {}) => {
      setState({ busy: format, error: null, last: null });
      try {
        let job = await call(
          api.POST('/exports', { body: { page_id: pageId, format, language: currentLanguage().code, params: params as any } }),
        );
        for (let i = 0; i < 120 && job.status !== 'done' && job.status !== 'failed'; i++) {
          await new Promise((r) => setTimeout(r, 1000));
          job = await call(api.GET('/exports/{id}', { params: { path: { id: job.id } } }));
        }
        if (job.status !== 'done' || !job.download_url)
          throw new ApiError(500, 'internal', job.error || t('app.export.failed', 'Export failed'));
        const a = document.createElement('a');
        a.href = job.download_url;
        a.download = job.file_name || '';
        document.body.appendChild(a);
        a.click();
        a.remove();
        setState({ busy: null, error: null, last: job });
        return job;
      } catch (e) {
        setState({ busy: null, error: e instanceof Error ? e.message : String(e), last: null });
        return null;
      }
    },
    [pageId],
  );
  return { ...state, run };
}

/** Badges of the settings navigation from GET /setup-health (completion, markup set, LP swap stale). */
export function useSettingsBadges(): Record<string, string> {
  const { session } = useSession();
  const q = useApi(['setup-health'], () => call(api.GET('/setup-health')), {
    enabled: can(session, 'page.settings.setup_health'),
    staleTime: 60_000,
  });
  const d = q.data as any;
  if (!d) return {};
  const item = (s: string) => d.items?.find((x: any) => x.setting === s);
  const markup = item('costs.markup');
  const lp = item('costs.lp_swap');
  const m = markup?.detail?.match(/(\d+) of (\d+)/);
  const stale = lp?.detail_params?.stale ?? lp?.detail?.match(/(\d+) stale/)?.[1];
  return {
    health: d.completion_pct < 100 ? Math.round(d.completion_pct) + '%' : '',
    markup: m && m[1] !== m[2] ? m[1] + '/' + m[2] : '',
    lpswap: stale ? t('app.settings-nav.n-stale', '{n} stale', { n: stale }) : '',
  };
}

/** SettingsNav with the session's permissions and live badges (settings pages). */
export function LiveSettingsNav({ active }: { active: string }) {
  const { session } = useSession();
  const badges = useSettingsBadges();
  return <SettingsNav active={active} permissions={session?.permissions || []} badges={badges} />;
}

/** Downloads an .ics calendar event (What's New webinars: "Add to calendar"). */
export function downloadIcs(item: {
  id: string;
  title: string;
  summary?: string;
  event_start?: string | null;
  event_end?: string | null;
  cta?: { url: string }[];
}) {
  if (!item.event_start) return;
  const f = (iso: string) =>
    new Date(iso)
      .toISOString()
      .replace(/[-:]/g, '')
      .replace(/\.\d{3}/, '');
  const end = item.event_end || new Date(Date.parse(item.event_start) + 3600_000).toISOString();
  const esc = (x: string) => x.replace(/[\\;,]/g, (c) => '\\' + c).replace(/\n/g, '\\n');
  const ics = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Dux FX//Brainux//EN',
    'BEGIN:VEVENT',
    'UID:' + item.id + '@brainux',
    'DTSTAMP:' + f(new Date().toISOString()),
    'DTSTART:' + f(item.event_start),
    'DTEND:' + f(end),
    'SUMMARY:' + esc(item.title),
    item.summary ? 'DESCRIPTION:' + esc(item.summary) : '',
    item.cta?.[0]?.url ? 'URL:' + item.cta[0].url : '',
    'END:VEVENT',
    'END:VCALENDAR',
  ]
    .filter(Boolean)
    .join('\r\n');
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([ics], { type: 'text/calendar' }));
  a.download = item.id + '.ics';
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}
